import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';

interface Props {
  sale: any;
  onClose: () => void;
}

const POSReceiptModal: React.FC<Props> = ({ sale, onClose }) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt - ${sale.sale_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              width: 80mm; 
              max-width: 80mm;
              padding: 3mm;
              font-size: 11px;
              line-height: 1.3;
              color: #000;
            }
            .center { text-align: center; }
            .right { text-align: right; }
            .bold { font-weight: bold; }
            .divider { border-top: 1px dashed #000; margin: 3px 0; }
            .double-divider { border-top: 2px solid #000; margin: 3px 0; }
            .row { display: flex; justify-content: space-between; }
            .item-row { margin-bottom: 2px; }
            .total-row { font-size: 13px; font-weight: bold; }
            .small { font-size: 9px; }
            .store-name { font-size: 14px; font-weight: bold; letter-spacing: 1px; }
            @media print {
              body { width: 80mm; max-width: 80mm; }
              @page { margin: 0; size: 80mm auto; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() { 
              setTimeout(function() { window.print(); window.close(); }, 200);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Actions */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-800">Receipt</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500"
            >
              <Printer size={16} />
              Print
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div ref={receiptRef} className="p-6 font-mono text-xs text-gray-800">
          {/* Store Header */}
          <div className="center mb-2">
            <p className="store-name">SIGNATURE STYLES</p>
            <p className="small">Yangon, Myanmar</p>
            <p className="small">Tel: 09-xxx-xxx-xxx</p>
          </div>

          <div className="divider"></div>

          {/* Sale Info */}
          <div style={{margin: '3px 0'}}>
            <div className="row"><span>Receipt #:</span><span className="bold">{sale.sale_number}</span></div>
            <div className="row"><span>Date:</span><span>{new Date(sale.completed_at || sale.created_at).toLocaleString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span></div>
            <div className="row"><span>Cashier:</span><span>{sale.cashier?.name || '-'}</span></div>
            {sale.customer_name && (
              <div className="row"><span>Customer:</span><span>{sale.customer_name}</span></div>
            )}
          </div>

          <div className="divider"></div>

          {/* Column Headers */}
          <div className="row bold" style={{marginBottom: '2px'}}>
            <span>Item</span>
            <span>Amount</span>
          </div>
          <div className="divider"></div>

          {/* Items */}
          <div style={{margin: '3px 0'}}>
            {sale.items?.map((item: any, idx: number) => (
              <div key={idx} className="item-row">
                <p className="bold">{item.product_name}</p>
                {item.size_name && <p className="small" style={{color:'#555'}}>&nbsp;&nbsp;{item.size_name}</p>}
                <div className="row">
                  <span>&nbsp;&nbsp;{item.quantity} × {Number(item.unit_price).toLocaleString()}</span>
                  <span>{Number(item.total).toLocaleString()}</span>
                </div>
                {Number(item.discount) > 0 && (
                  <div className="row small" style={{color:'#555'}}>
                    <span>&nbsp;&nbsp;Discount</span>
                    <span>-{Number(item.discount).toLocaleString()}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="divider"></div>

          {/* Totals */}
          <div style={{margin: '3px 0'}}>
            <div className="row"><span>Subtotal</span><span>{Number(sale.subtotal).toLocaleString()} MMK</span></div>
            {Number(sale.item_discount_total) > 0 && (
              <div className="row"><span>Item Discount</span><span>-{Number(sale.item_discount_total).toLocaleString()} MMK</span></div>
            )}
            {Number(sale.order_discount) > 0 && (
              <div className="row"><span>Order Discount</span><span>-{Number(sale.order_discount).toLocaleString()} MMK</span></div>
            )}
          </div>

          <div className="double-divider"></div>

          <div className="row total-row" style={{margin: '4px 0'}}>
            <span>TOTAL</span>
            <span>{Number(sale.total_amount).toLocaleString()} MMK</span>
          </div>

          <div className="divider"></div>

          <div style={{margin: '3px 0'}}>
            <div className="row">
              <span>Payment ({sale.payment_method?.replace('_', ' ')})</span>
              <span>{Number(sale.amount_received).toLocaleString()} MMK</span>
            </div>
            {Number(sale.change_amount) > 0 && (
              <div className="row bold">
                <span>Change</span>
                <span>{Number(sale.change_amount).toLocaleString()} MMK</span>
              </div>
            )}
          </div>

          <div className="divider"></div>

          {/* Footer */}
          <div className="center" style={{marginTop: '6px'}}>
            <p>Thank you for your purchase!</p>
            <p className="small" style={{marginTop: '3px'}}>Exchange within 7 days with receipt</p>
            <p className="small" style={{marginTop: '6px'}}>— Signature Styles —</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSReceiptModal;
